"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type ClipboardEvent as ReactClipboardEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from "react";
import { serializeComposer } from "./serialize";
import { maybeAutolist } from "./autolist";
import { cn } from "@/lib/utils";
import type { HeightAnimator } from "./height";

/**
 * The rich composer editor (GPT-feel, founder-approved 08-13): a
 * contenteditable line that types big, self-forms lists ("1. " / "- " at a
 * line start; Shift+Enter continues numbering via insertParagraph, an empty
 * item exits), and reports its serialized plain text + multiline state to its
 * host (the home's box, BashPill). Enter sends. The host owns its own layout;
 * this component owns the editing surface and drives the shared height
 * animator on every growth.
 */

export interface RichEditorHandle {
  clear(): void;
  focus(): void;
  /** Replace the editor's contents (first-run starters); caret lands at the end. */
  setText(text: string): void;
  /** The editing surface itself: a host anchors a caret list to it (the @ and
   *  / pickers on a task's screen) and reads the caret off it (caret.ts). */
  element(): HTMLDivElement | null;
}

export const RichEditor = forwardRef<
  RichEditorHandle,
  {
    placeholder: string;
    ariaLabel: string;
    onTextChange: (text: string) => void;
    onSend: () => void;
    onMultiChange: (multi: boolean) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    /** The Dock's file-intake paste hook — runs first; a file paste consumes the
     *  event and no text is inserted. */
    onPasteFiles?: (e: ReactClipboardEvent) => void;
    /** Every key the editor does not take itself (Enter is its own: the send,
     *  the Shift+Enter paragraph); the host hears the rest here, on the
     *  element that has focus, never on a wrapper. */
    onKeyDown?: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
    heightAnimator?: HeightAnimator;
    /** The host's own classes, merged last (the box gives it the box's line
     *  and the typed example's hide-until-awake trick). */
    className?: string;
  }
>(function RichEditor(
  {
    placeholder,
    ariaLabel,
    onTextChange,
    onSend,
    onMultiChange,
    onFocus,
    onBlur,
    onPasteFiles,
    onKeyDown,
    heightAnimator,
    className,
  },
  ref
): ReactElement {
  const editorRef = useRef<HTMLDivElement>(null);
  const multiRef = useRef(false);
  const pillWidthRef = useRef(0);

  const sync = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    // No visible text = empty, whatever tags remain. Chrome leaves husks
    // behind full deletions — <br>, <div><br></div>, and (select-all delete
    // inside a list) a whole ghost <ol><li><br></li></ol> that kept the bar
    // half-morphed over the placeholder (founder-hit 08-13 round 5). Wipe it
    // all so the placeholder and the compact pill come back together.
    if ((el.textContent ?? "").trim() === "" && el.innerHTML !== "") {
      el.innerHTML = "";
    }
    const text = serializeComposer(el);
    const empty = el.innerHTML === "";
    el.classList.toggle("is-empty", empty);
    // Multiline = any block structure, or the single line wrapping — where
    // "wrapping" is ALWAYS judged at the pill's input width. The card's input
    // is wider (it spans the row the pill shares with attach/pickers/send), so
    // judging at the current width oscillates: text wraps in the pill → card →
    // now fits one wide line → pill → wraps again, one flip per keystroke
    // until the text overflows even the card (founder-hit 08-18 jitter).
    const hasBlocks = !!el.querySelector("div,p,ol,ul,li,br");
    let wraps = false;
    if (!hasBlocks) {
      if (!multiRef.current && el.clientWidth > 0)
        pillWidthRef.current = el.clientWidth;
      if (multiRef.current && pillWidthRef.current > 0) {
        // max-width, not width: flex-grow would stretch straight past an
        // inline width. Synchronous set-measure-restore — nothing paints.
        const prev = el.style.maxWidth;
        el.style.maxWidth = `${pillWidthRef.current}px`;
        wraps = el.scrollHeight > 34;
        el.style.maxWidth = prev;
      } else {
        wraps = el.scrollHeight > 34;
      }
    }
    const multi = hasBlocks || wraps;
    const multiChanged = multi !== multiRef.current;
    if (multiChanged) {
      multiRef.current = multi;
      // The morph edge: the Dock flips its card class inside the animator's
      // mutate (flushSync), which owns THIS round's height tween — a second
      // animate() here would cancel it mid-flight and snap.
      onMultiChange(multi);
    }
    onTextChange(text);
    if (!multiChanged) heightAnimator?.animate();
  }, [onTextChange, onMultiChange, heightAnimator]);

  useImperativeHandle(
    ref,
    () => ({
      clear() {
        const el = editorRef.current;
        if (!el) return;
        el.innerHTML = "";
        sync();
      },
      focus() {
        editorRef.current?.focus();
      },
      setText(text: string) {
        const el = editorRef.current;
        if (!el) return;
        el.textContent = text;
        sync();
        // Caret to the end so typing continues the prompt, not prepends it.
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      },
      element() {
        return editorRef.current;
      },
    }),
    [sync]
  );

  // Initial empty state (the ::before placeholder needs the class).
  useEffect(() => {
    editorRef.current?.classList.add("is-empty");
  }, []);

  return (
    <div
      ref={editorRef}
      className={cn(
        "flex-1 ml-1.5 border-none bg-transparent outline-none focus-visible:outline-none font-inter text-3.5 text-fg-default min-w-0 [resize:none] leading-normal min-h-5.5 max-h-[42vh] overflow-y-auto py-0.5 px-0 [white-space:pre-wrap] [word-break:break-word] [caret-color:var(--fg-default)] focus:outline-none focus:shadow-none focus-visible:outline-none focus-visible:shadow-none max-md:text-4 [&.is-empty::before]:[content:attr(data-ph)] [&.is-empty::before]:text-fg-subtle [&.is-empty::before]:pointer-events-none [&_>_div]:my-0.5 [&_>_div]:mx-0 [&_>_p]:my-0.5 [&_>_p]:mx-0 [&_ol]:[list-style:decimal] [&_ul]:[list-style:disc] [&_ol]:my-1 [&_ol]:mx-0 [&_ol]:pl-6.5 [&_ul]:my-1 [&_ul]:mx-0 [&_ul]:pl-6.5 [&_li]:my-1 [&_li]:mx-0 [&_li]:pl-1 [&_li::marker]:text-fg-muted [&_li::marker]:[font-variant-numeric:tabular-nums] [&_ul_li::marker]:text-[0.9em]",
        className
      )}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={ariaLabel}
      data-ph={placeholder}
      onInput={(e) => {
        const native = e.nativeEvent as InputEvent;
        if (native.inputType === "insertText" && native.data === " ") {
          maybeAutolist(editorRef.current!);
        }
        sync();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Enter") {
          onKeyDown?.(e);
          return;
        }
        // A composing Enter is the IME's own key (it commits a pinyin, romaji
        // or Indic word): it never sends and never starts a paragraph, and it
        // is left alone. Safari fires it after compositionend with isComposing
        // false, so the legacy 229 is read too (R-F15).
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;
        e.preventDefault();
        if (e.shiftKey) {
          // insertParagraph continues a numbered list with the next number and
          // exits from an empty item — the notebook feel, no code needed.
          document.execCommand?.("insertParagraph");
          sync();
        } else {
          onSend();
        }
      }}
      onPaste={(e) => {
        onPasteFiles?.(e);
        if (e.defaultPrevented) return;
        // Text pastes land as PLAIN text — outside HTML must not enter the
        // message DOM (its serialization would silently drop structure).
        const text = e.clipboardData?.getData("text/plain");
        if (text) {
          e.preventDefault();
          document.execCommand?.("insertText", false, text);
          sync();
        }
      }}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
});
